<template>
  <nav class="navbar navbar-expand-lg navbar-light">
    <div class="container">
      <router-link to="/" class="navbar-brand">Sparkforge Games</router-link>

      <!-- Account sits OUTSIDE the collapse so it is one tap on mobile.
           Inside it, the hamburger opened a menu whose only content was
           another menu button — two taps to reach Settings. -->
      <div class="d-flex align-items-center order-lg-last ms-auto">
          <div v-if="!user" class="d-flex guest-actions">
            <router-link to="/login" class="btn btn-outline-primary me-2">Sign in</router-link>
            <router-link to="/register" class="btn btn-primary">Join</router-link>
          </div>
          <div v-else class="d-flex align-items-center">
            <div class="dropdown">
              <button class="btn btn-link text-decoration-none dropdown-toggle d-flex align-items-center" 
                type="button" 
                id="userDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
              >
                <img
                  v-if="user.photoURL"
                  :src="user.photoURL"
                  class="rounded-circle me-2 account-avatar"
                  alt=""
                />
                <span v-else class="rounded-circle me-2 account-avatar account-initial">
                  {{ (user.displayName || user.email || '?').charAt(0).toUpperCase() }}
                </span>
                <span class="account-name">{{ user.displayName || user.email }}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li>
                  <router-link to="/settings" class="dropdown-item">
                    <i class="bi bi-gear me-2"></i> Settings
                  </router-link>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <button @click="logout" class="dropdown-item">
                    <i class="bi bi-box-arrow-right me-2"></i> Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>
      </div>
      <div class="navbar-links">
        <ul class="navbar-nav">
          <li class="nav-item">
            <router-link to="/" class="nav-link">Home</router-link>
          </li>
          <!-- Signed-in only: `/` is the public landing page, `/main` is the
               workshop, and /board-games is the whole library. -->
          <li class="nav-item" v-if="user">
            <router-link to="/main" class="nav-link">My Workshop</router-link>
          </li>
          <li class="nav-item" v-if="user">
            <router-link to="/board-games" class="nav-link">Board Games</router-link>
          </li>
          <!-- Boards belong to a game, so this follows whichever game you are
               in. Off the game pages there is nothing for it to point at, and
               a link that lands on an error is worse than one that waits. -->
          <li v-if="boardsLink" class="nav-item">
            <router-link :to="boardsLink" class="nav-link">Boards</router-link>
          </li>
          <!-- <li class="nav-item">
            <router-link to="/Instruction" class="nav-link">About</router-link>
          </li>
          <li class="nav-item">
            <router-link to="/build-boards" class="nav-link">TBD</router-link>
          </li>
          <li class="nav-item">
            <router-link to="/image-generator" class="nav-link">TBD</router-link>
          </li> -->
        </ul>
      </div>
    </div>
  </nav>
  <router-view></router-view>
</template>

<script>
import { ref, onMounted } from 'vue';
import { signInWithGoogle, signOutUser, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

export default {
  name: 'App',
  computed: {
    /**
     * The current game, read from whichever route names one. Cards use a
     * query string, boards and the game page use params, and a board or tile
     * page carries its game in the store rather than the URL — those fall back
     * to no link rather than guessing.
     */
    boardsLink() {
      const r = this.$route;
      const id =
        (r.name === 'BoardGameDetail' && r.params.id) ||
        r.params.boardGameId ||
        r.query.boardGameId ||
        null;
      return id ? { name: 'BuildBoards', params: { boardGameId: id } } : null;
    }
  },
  setup() {
    const user = ref(null);

    const login = async () => {
      try {
        const loggedInUser = await signInWithGoogle();
        user.value = loggedInUser;
      } catch (error) {
        console.error('Login failed', error);
      }
    };

    const logout = async () => {
      try {
        await signOutUser();
        user.value = null;
      } catch (error) {
        console.error('Logout failed', error);
      }
    };

    onMounted(() => {
      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        user.value = currentUser;
      });

      // Unsubscribe from listener when component is unmounted
      return () => unsubscribe();
    });

    return {
      user,
      login,
      logout
    };
  }
}
</script>

<style>
/* --- Account menu (mobile) ------------------------------------------ */
.account-avatar {
  width: 30px;
  height: 30px;
  object-fit: cover;
  flex: 0 0 auto;
}
/* No external placeholder service: via.placeholder.com is dead and rendered
   as a broken-image icon in the navbar. An initial needs no network. */
.account-initial {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--pop-orange, #e8590c);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1;
}

/* One tap to Settings/Logout. A long display name must not push the
   hamburger off the edge, so it truncates rather than wraps. */
.navbar .dropdown-toggle {
  max-width: 46vw;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
.navbar .dropdown-toggle .account-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  /* Below the smallest phone width the name is not worth the space — the
     avatar and the chevron already say what the control is. Target the name
     specifically: `> span` also matched the initial avatar, which left an
     empty gap where the avatar should be. */
  .navbar .dropdown-toggle .account-name {
    display: none;
  }
  .navbar-brand {
    font-size: 1.15rem !important;
  }
}

#app {
  font-family: var(--font-body, 'Inter', sans-serif);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  background-color: var(--bg-paper);
  position: relative;
}

/* The links used to sit inside a Bootstrap collapse whose toggler was removed,
   which left them hidden below lg. They are two short words, so they simply
   stay visible. */
.navbar-links .navbar-nav {
  flex-direction: row;
  gap: 0.25rem;
}
.navbar-links .nav-link {
  padding: 0.35rem 0.6rem;
}
.navbar-links .nav-link.router-link-exact-active {
  color: var(--pop-orange, #ff6b35);
  font-weight: 600;
}

/* Guest bar: two full-size buttons plus a toggler wrapped the navbar onto two
   rows on a phone. */
@media (max-width: 575.98px) {
  .guest-actions .btn {
    padding: 0.3rem 0.7rem;
    font-size: 0.9rem;
  }
}
</style>
